/**
 * Latency Compensation Settings
 * Allows musicians to fine-tune audio sync in jam sessions
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import {
  Settings,
  Activity,
  Gauge,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Zap,
  Clock,
  Radio,
  Check,
  AlertCircle,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

// Latency quality indicators
const getLatencyQuality = (ms) => {
  if (ms < 20) return { label: 'Excellent', color: 'text-emerald-400', bg: 'bg-emerald-500' };
  if (ms < 50) return { label: 'Good', color: 'text-green-400', bg: 'bg-green-500' };
  if (ms < 100) return { label: 'Fair', color: 'text-amber-400', bg: 'bg-amber-500' };
  if (ms < 200) return { label: 'Poor', color: 'text-orange-400', bg: 'bg-orange-500' };
  return { label: 'Bad', color: 'text-red-400', bg: 'bg-red-500' };
};

// Latency bar visualization
const LatencyBar = ({ value, max = 200, label }) => {
  const quality = getLatencyQuality(value);
  const percentage = Math.min((value / max) * 100, 100);
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-zinc-400">{label}</span>
        <span className={quality.color}>{value}ms</span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          className={`h-full ${quality.bg} rounded-full`}
        />
      </div>
    </div>
  );
};

export const LatencySettings = ({
  isOpen,
  onClose,
  session,
  userId,
  onSend,
  currentLatency = {},
}) => {
  const [mode, setMode] = useState(session?.settings?.latency_compensation?.mode || 'auto');
  const [globalOffset, setGlobalOffset] = useState(
    session?.settings?.latency_compensation?.global_offset_ms || 0
  );
  const [personalOffset, setPersonalOffset] = useState(currentLatency?.latency_offset || 0);
  const [bufferSize, setBufferSize] = useState(
    session?.settings?.latency_compensation?.buffer_size || 256
  );
  const [jitterBuffer, setJitterBuffer] = useState(
    session?.settings?.latency_compensation?.jitter_buffer_ms || 50
  );
  
  // Measured latencies
  const [serverLatency, setServerLatency] = useState(null);
  const [peerLatencies, setPeerLatencies] = useState({});
  const [isTesting, setIsTesting] = useState(false);
  const [testProgress, setTestProgress] = useState(0);
  
  const pingIntervalRef = useRef(null);
  const testTimeoutRef = useRef(null);

  // Run latency test to server
  const testServerLatency = useCallback(() => {
    const start = performance.now();
    onSend?.({ type: 'ping', timestamp: start });
    
    // The pong response will come back via handleMessage
    return start;
  }, [onSend]);

  // Run comprehensive latency test
  const runLatencyTest = async () => {
    setIsTesting(true);
    setTestProgress(0);
    
    const measurements = [];
    const iterations = 5;
    
    for (let i = 0; i < iterations; i++) {
      const start = performance.now();
      onSend?.({ type: 'ping', timestamp: start });
      
      // Wait for response (handled externally)
      await new Promise(resolve => setTimeout(resolve, 200));
      setTestProgress(((i + 1) / iterations) * 100);
    }
    
    // Calculate average from pong responses stored in state
    setIsTesting(false);
    toast.success('Latency test complete');
  };

  // Apply latency settings
  const applySettings = () => {
    onSend?.({
      type: 'update_latency',
      latency_offset: personalOffset,
      input_latency: serverLatency || 0,
      output_latency: serverLatency || 0,
    });
    
    // If host, update global settings
    if (session?.host_id === userId) {
      onSend?.({
        type: 'update_settings',
        settings: {
          latency_compensation: {
            enabled: true,
            mode,
            global_offset_ms: globalOffset,
            buffer_size: bufferSize,
            jitter_buffer_ms: jitterBuffer,
          }
        }
      });
    }
    
    toast.success('Latency settings applied');
    onClose?.();
  };

  // Auto-calculate optimal offset
  const autoCalculateOffset = () => {
    if (serverLatency) {
      // Recommend offset based on measured latency
      const recommended = Math.round(serverLatency / 2);
      setPersonalOffset(recommended);
      toast.info(`Recommended offset: ${recommended}ms`);
    } else {
      toast.error('Run latency test first');
    }
  };

  // Handle incoming pong messages
  useEffect(() => {
    const handlePong = (event) => {
      if (event.detail?.type === 'pong') {
        const now = performance.now();
        const latency = Math.round(now - event.detail.timestamp);
        setServerLatency(latency);
      }
    };
    
    window.addEventListener('jam-pong', handlePong);
    return () => window.removeEventListener('jam-pong', handlePong);
  }, []);

  if (!isOpen) return null;

  const isHost = session?.host_id === userId;
  const currentParticipant = session?.participants?.find(p => p.user_id === userId);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-zinc-900 rounded-2xl border border-zinc-700 shadow-2xl overflow-hidden"
          data-testid="latency-settings-modal"
        >
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-violet-500/20 via-purple-500/20 to-fuchsia-500/20 border-b border-zinc-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Latency Compensation</h2>
                <p className="text-sm text-zinc-400">Fine-tune your audio sync</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Current Latency Status */}
            <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-violet-400" />
                  Current Latency
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={runLatencyTest}
                  disabled={isTesting}
                  className="text-violet-400 hover:text-violet-300"
                  data-testid="test-latency-btn"
                >
                  {isTesting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  <span className="ml-1">Test</span>
                </Button>
              </div>
              
              {isTesting && (
                <div className="mb-3">
                  <div className="h-1 bg-zinc-700 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${testProgress}%` }}
                      className="h-full bg-violet-500"
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">Testing latency...</p>
                </div>
              )}
              
              <div className="space-y-2">
                <LatencyBar
                  value={serverLatency || currentLatency?.input_latency || 0}
                  label="Server Round-trip"
                />
                <LatencyBar
                  value={currentParticipant?.input_latency || 0}
                  label="Input Latency"
                />
                <LatencyBar
                  value={currentParticipant?.output_latency || 0}
                  label="Output Latency"
                />
              </div>
              
              {serverLatency && (
                <div className="mt-3 flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getLatencyQuality(serverLatency).bg}`} />
                  <span className={`text-sm ${getLatencyQuality(serverLatency).color}`}>
                    {getLatencyQuality(serverLatency).label} connection
                  </span>
                </div>
              )}
            </div>

            {/* Compensation Mode */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Compensation Mode
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'auto', label: 'Auto', icon: Zap, desc: 'Automatic adjustment' },
                  { value: 'manual', label: 'Manual', icon: Settings, desc: 'Set your own offset' },
                  { value: 'adaptive', label: 'Adaptive', icon: Radio, desc: 'Dynamic adjustment' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setMode(option.value)}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      mode === option.value
                        ? 'border-violet-500 bg-violet-500/20 text-violet-400'
                        : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                    }`}
                    data-testid={`mode-${option.value}`}
                  >
                    <option.icon className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Personal Offset Slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-zinc-300">
                  Personal Offset
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-violet-400 font-mono">{personalOffset}ms</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={autoCalculateOffset}
                    className="text-xs text-zinc-400 hover:text-white"
                  >
                    Auto
                  </Button>
                </div>
              </div>
              <Slider
                value={[personalOffset]}
                onValueChange={([val]) => setPersonalOffset(val)}
                min={-100}
                max={100}
                step={1}
                className="w-full"
                data-testid="personal-offset-slider"
              />
              <div className="flex justify-between text-xs text-zinc-500 mt-1">
                <span>Earlier (-100ms)</span>
                <span>Later (+100ms)</span>
              </div>
              <p className="text-xs text-zinc-500 mt-2 flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                Negative values make your audio earlier in the mix, positive values make it later.
              </p>
            </div>

            {/* Host-only: Global Settings */}
            {isHost && (
              <>
                <div className="border-t border-zinc-800 pt-4">
                  <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                    <Settings className="w-4 h-4 text-amber-400" />
                    Host Settings (applies to all)
                  </h3>
                  
                  {/* Global Offset */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-zinc-400">Global Offset</label>
                      <span className="text-sm text-amber-400 font-mono">{globalOffset}ms</span>
                    </div>
                    <Slider
                      value={[globalOffset]}
                      onValueChange={([val]) => setGlobalOffset(val)}
                      min={-200}
                      max={200}
                      step={5}
                      className="w-full"
                    />
                  </div>
                  
                  {/* Buffer Size */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-zinc-400">Buffer Size</label>
                      <span className="text-sm text-zinc-300 font-mono">{bufferSize} samples</span>
                    </div>
                    <div className="flex gap-2">
                      {[64, 128, 256, 512, 1024].map((size) => (
                        <button
                          key={size}
                          onClick={() => setBufferSize(size)}
                          className={`flex-1 py-1.5 rounded-lg text-xs transition-all ${
                            bufferSize === size
                              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50'
                              : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-500'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      Lower = less latency, higher = more stability
                    </p>
                  </div>
                  
                  {/* Jitter Buffer */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-zinc-400">Jitter Buffer</label>
                      <span className="text-sm text-zinc-300 font-mono">{jitterBuffer}ms</span>
                    </div>
                    <Slider
                      value={[jitterBuffer]}
                      onValueChange={([val]) => setJitterBuffer(val)}
                      min={10}
                      max={200}
                      step={10}
                      className="w-full"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Higher values reduce audio glitches on unstable connections
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Tips */}
            <div className="p-3 bg-violet-500/10 border border-violet-500/20 rounded-xl">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-zinc-300">
                  <p className="font-medium text-violet-400 mb-1">Pro Tips</p>
                  <ul className="space-y-1 text-zinc-400">
                    <li>• Run the latency test before each session</li>
                    <li>• Use wired internet for best results</li>
                    <li>• Close other bandwidth-heavy apps</li>
                    <li>• Lower buffer size if latency is too high</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-zinc-800 flex gap-3">
            <Button
              variant="ghost"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={applySettings}
              className="flex-1 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600"
              data-testid="apply-latency-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Apply Settings
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LatencySettings;
